import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import ProjectViewer from './ProjectViewer';
import FooterPanel from './FooterPanel';
import DetailsModal from './DetailsModal';
import AboutModal from './AboutModal';
import ContactModal from './ContactModal';
import '../MainPage.css';

export const projects = [
    {
        id: 1,
        title: "Yellow Ducky Marketplace",
        projectPrefix: "Laravel Project - ",
        description: "A live Chinese-language classifieds & e-commerce marketplace.",
        longDescription: "Yellow Ducky (yellowducky.ca) is a production e-commerce and classifieds marketplace built for the local Chinese community in Canada, and it is deployed and live today. It is a heavily customised Laravel platform serving a Simplified-Chinese audience: sellers publish listings with images and video, buyers browse and search entirely in Chinese, and paid VIP memberships are billed end to end through Stripe. Beyond the storefront it ships a custom admin panel, a Chinese-aware search engine (MySQL ngram full-text indexing with Traditional-to-Simplified normalisation and synonym expansion), an asynchronous FFmpeg pipeline that transcodes every uploaded video into a web-friendly 720p H.264 file with a generated poster frame, and complete technical SEO with Chinese meta tags, JSON-LD structured data and generated sitemaps. It runs on an Ubuntu VPS behind nginx over HTTPS, deployed automatically through GitHub Actions.",
        stack: [
            { category: "Framework", details: "Laravel 13 (PHP 8.3)" },
            { category: "Payments", details: "Stripe + Laravel Cashier (VIP subscriptions)" },
            { category: "Database", details: "MySQL 8 with ngram FULLTEXT indexes" },
            { category: "Search", details: "OpenCC Chinese normalisation + synonym expansion" },
            { category: "Media", details: "FFmpeg transcoding queue, Intervention Image" },
            { category: "Mail", details: "Resend API (outbound), Microsoft 365 (inbound)" },
            { category: "Frontend", details: "Blade, Bootstrap 5, SCSS, Vite" },
            { category: "Infrastructure", details: "Ubuntu VPS, nginx, HTTPS, GitHub Actions CI/CD" }
        ],
        url: "https://yellowducky.ca",
        // Ships <base target="_top">, so its links can never stay inside the
        // frame - offer the real site instead of a browse-here prompt.
        openExternally: true,
        // Shown on phones in place of the frame (see ProjectViewer)
        mobilePreview: "/assets/yellowducky-mobile.jpg",
        github: "https://github.com/joshuazhang447/ProjectDucky",
        type: "iframe",
        dates: "August 2026",
        theme: {
            gradient: "linear-gradient(90deg, #FFC93C, #FF8F00)"
        }
    },
    {
        id: 2,
        title: "LavenderBook",
        projectPrefix: "React Native Project - ",
        description: "An LGBTQ+ map for rating venues and finding clinics, shelters and services.",
        longDescription: "LavenderBook is a community map of how safe and welcoming real places are for LGBTQ+ people, and trans people in particular, built for One Among Us, an East Asian transgender support organization. Members rate venues on LGBTQ friendliness, answer a trans bathroom question, and answer questions set for each kind of place, from clinics to shelters. One codebase runs on Android, iOS and the web. Privacy comes first for users in high-risk countries: pseudonymous reviews, week-only dates, location rounded on the device and a \"Leave now\" exit, with every permission enforced by PostgreSQL row-level security.",
        stack: [
            { category: "Framework", details: "Expo SDK 57, React Native 0.86, React 19.2 (TypeScript)" },
            { category: "Routing", details: "Expo Router" },
            { category: "Interface", details: "NativeWind 4 + Tailwind CSS, react-native-reusables, Reanimated" },
            { category: "Maps", details: "react-native-maps, @vis.gl/react-google-maps, Google Places API" },
            { category: "Backend", details: "Supabase (PostgreSQL, PostgREST, Auth, Realtime)" },
            { category: "Security", details: "Row-level security, Deno edge functions for the Places key" },
            { category: "Build & Hosting", details: "EAS Build (Android), Cloudflare (web)" }
        ],
        url: "https://lavenderbook.oau.app/",
        // Its buttons stop working inside any sandboxed frame (see ProjectViewer)
        sandbox: false,
        github: "https://github.com/joshuazhang447/LavanderBook",
        type: "iframe",
        dates: "September 2026 – Present",
        theme: {
            // Trans flag blue and pink
            gradient: "linear-gradient(90deg, #5BCEFA, #F5A9B8, #5BCEFA)"
        }
    },
    {
        id: 3,
        title: "Circle 2 Lab Publisher Site (Capstone Project)",
        projectPrefix: "Angular Project - ",
        description: "A publishing site for the game studio Circle 2 Lab.",
        longDescription: "Circle 2 Lab Publisher Site is the official publishing and news site for the indie game studio Circle 2 Lab, built as a capstone project. It is an Angular 21 single page application where the main sections are stitched together as full-screen Swiper slides, so navigating between the blog, studio and contact pages feels like sliding through one continuous surface rather than loading separate pages. The blog runs on a file-based CMS: a JSON manifest drives a paginated index, per-post pages and cover art, with no backend to maintain. The whole site is trilingual (English, Japanese and Simplified Chinese) through ngx-translate, and it is themed with a custom DaisyUI palette built around the studio brand pink, layered on Tailwind CSS v4.",
        stack: [
            { category: "Framework", details: "Angular 21 (standalone components, TypeScript 5.9)" },
            { category: "Routing", details: "Angular Router + animated route transitions" },
            { category: "Styling", details: "Tailwind CSS v4 + DaisyUI 5 (custom theme)" },
            { category: "Internationalisation", details: "ngx-translate (EN / JA / ZH)" },
            { category: "UI", details: "Swiper full-page slides, progressbar.js" },
            { category: "Content", details: "JSON manifest-driven blog with pagination" },
            { category: "Testing", details: "Vitest + jsdom" }
        ],
        url: "https://circle2.moe/blog",
        github: "https://github.com/joshuazhang447/circle2lab-website",
        type: "iframe",
        dates: "April 2026",
        theme: {
            gradient: "linear-gradient(90deg, #E1368C, #EC2F2F)"
        }
    },
    {
        id: 4,
        title: "Pretendo Classic",
        projectPrefix: "Angular Project - ",
        description: "Replicating the NES Classic console.",
        longDescription: "Pretendo Classic is a Single Page Application (SPA) designed to replicate the nostalgia of the NES Classic console.",
        stack: [
            { category: "Framework", details: "Angular" },
            { category: "Routing", details: "Angular Router" },
            { category: "Data Management", details: "Angular In-Memory Web API" },
            { category: "Emulation", details: "JSNES" },
            { category: "Styling", details: "CSS/SCSS" }
        ],
        url: "https://joshua-zhang-learning-angular.vercel.app/game",
        github: "https://github.com/joshuazhang447/pretendo-classic",
        type: "iframe",
        dates: "September 2025",
        theme: {
            gradient: "linear-gradient(90deg, #D32F2F, #FFCDD2)"
        }
    },
    {
        id: 5,
        title: "Moko Reader",
        projectPrefix: "Electron Project - ",
        description: "A dynamic reader for books and comics.",
        longDescription: "Moko Reader is a full-fledged, dynamic reader application designed for a seamless reading experience for both text-based books and comics.",
        stack: [
            { category: "Framework", details: "Electron, Node" },
            { category: "Routing", details: "Angular Router" },
            { category: "Reader", details: "JSZIP" },
            { category: "Styling", details: "CSS/SCSS" }
        ],
        url: "https://moko-reader.vercel.app/",
        github: "https://github.com/joshuazhang447/moko-reader",
        type: "iframe",
        dates: "October 2025",
        theme: {
            gradient: "linear-gradient(90deg, #FFC107, #2196F3)"
        }
    },
    // Resume Enhancer AI is hidden for now; uncomment to bring it back.
    // {
    //     id: 5,
    //     title: "Resume Enhancer AI",
    //     projectPrefix: "VanillaJS Project - ",
    //     description: "AI powered resume adjustment tool.",
    //     longDescription: "Resume Enhancer AI is an AI powered web app designed to adjust your resume to specific job opportunities.",
    //     stack: [
    //         { category: "Language", details: "HTML, JS" },
    //         { category: "API", details: "Gemini API" },
    //         { category: "Library", details: "PDF.js" },
    //         { category: "Styling", details: "CSS" }
    //     ],
    //     url: "https://resume-enhancer-lemon.vercel.app/",
    //     github: "https://github.com/joshuazhang447/resume-enhancer",
    //     type: "iframe",
    //     dates: "December 2025",
    //     theme: {
    //         gradient: "linear-gradient(90deg, #1E3C72, #2A5298)"
    //     }
    // },
    {
        id: 6,
        title: "Natsume Library Management",
        projectPrefix: "JavaFX Project - ",
        description: "Java-based library management system.",
        longDescription: "Natsume is a Java-based desktop application designed to manage library inventories, track circulation, and provide digital reading abilities. It features an easy to understand GUI built with JavaFX.",
        stack: [
            { category: "Language", details: "Java 21" },
            { category: "Framework", details: "JavaFX, BootstrapFX" },
            { category: "Database", details: "MariaDB" },
            { category: "Libraries", details: "Epub4j, KXML2" }
        ],
        url: "https://joshuazhang447.github.io/natsume-preview/",
        releaseLink: "https://github.com/joshuazhang447/ProjectNatsume/releases",
        releaseLabel: "Download App",
        github: "https://github.com/joshuazhang447/ProjectNatsume",
        type: "iframe",
        dates: "October 2025",
        theme: {
            gradient: "linear-gradient(135deg, #536976 0%, #292E49 100%)"
        }
    },
    {
        id: 7,
        title: "Monster Clicker Adventure",
        projectPrefix: "JavaFX Project - ",
        description: "Retro-styled idle RPG.",
        longDescription: "Monster Clicker Adventure is a retro-styled idle RPG built with Java 21 and JavaFX. Journey through lush pixel-art environments, battle waves of unique enemies, and upgrade your gear to become the ultimate monster hunter.",
        stack: [
            { category: "Language", details: "Java JDK 21" },
            { category: "Framework", details: "JavaFX" },
            { category: "Build Tool", details: "Gradle" },
            { category: "Assets", details: "CombatRPG, Sword Icons" }
        ],
        url: "https://monster-clicker-adventure-preview.vercel.app/",
        releaseLink: "https://github.com/joshuazhang447/monster-clicker-adventure/releases/tag/1.0.2",
        releaseLabel: "Download App",
        github: "https://github.com/joshuazhang447/monster-clicker-adventure",
        type: "iframe",
        dates: "April 2025",
        theme: {
            gradient: "linear-gradient(135deg, #8B0000 0%, #FF8C00 100%)"
        }
    },
    {
        id: 8,
        title: "Nexitally VPN",
        projectPrefix: "React Project - ",
        description: "A fast and secure VPN service.",
        longDescription: "Nexitally is a high-performance, premium VPN service merchant page, built with React 19. It delivers a seamless, secure, and modern navigation experience.",
        stack: [
            { category: "Framework", details: "React 19" },
            { category: "Routing", details: "React Router DOM v7" },
            { category: "Styling", details: "Tailwind CSS v3 (Utility-first) + DaisyUI" },
            { category: "Icons", details: "React Icons (Heroicons, FontAwesome)" }
        ],
        url: "https://nexitally-vpn.vercel.app/",
        github: "https://github.com/joshuazhang447/nexitally-vpn",
        type: "iframe",
        dates: "December 2025"
    },
];

// ... (projects array remains unchanged)

const MainPage = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showDetails, setShowDetails] = useState(false);
    const [showAbout, setShowAbout] = useState(false);
    const [showContact, setShowContact] = useState(false);
    // Touch only: whether the embedded site is taking touches (see ProjectViewer)
    const [frameActive, setFrameActive] = useState(false);

    const goToProject = (resolveIndex) => {
        setFrameActive(false);
        setCurrentIndex(resolveIndex);
    };

    const nextProject = () => {
        goToProject((prev) => (prev + 1) % projects.length);
    };

    const prevProject = () => {
        goToProject((prev) => (prev - 1 + projects.length) % projects.length);
    };

    return (
        <motion.div
            className="main-page-container"
            style={projects[currentIndex].theme?.gradient
                ? { '--project-gradient': projects[currentIndex].theme.gradient }
                : undefined}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9 }}
        >
            <header className="main-header">
                <div className="header-name">Joshua Z</div>
                <nav className="header-nav">
                    <Link to="/resume">Resume</Link>
                    <button type="button" onClick={() => setShowAbout(true)}>About me</button>
                    <button type="button" onClick={() => setShowContact(true)}>Contact</button>
                </nav>
            </header>

            {/* Dynamic Title */}
            <div className="project-title-container">
                <h2 className="project-title">
                    {projects[currentIndex].projectPrefix || ""}{projects[currentIndex].title}
                </h2>
            </div>

            <main className="main-viewport">
                <ProjectViewer
                    project={projects[currentIndex]}
                    active={frameActive}
                    onActivate={() => setFrameActive(true)}
                    onRelease={() => setFrameActive(false)}
                />
            </main>

            <FooterPanel
                project={projects[currentIndex]}
                totalProjects={projects.length}
                currentIndex={currentIndex}
                onNext={nextProject}
                onPrev={prevProject}
                onDotClick={(index) => goToProject(index)}
                onDetailsClick={() => setShowDetails(true)}
            />

            <DetailsModal
                isOpen={showDetails}
                onClose={() => setShowDetails(false)}
                project={projects[currentIndex]}
            />

            <AboutModal
                isOpen={showAbout}
                onClose={() => setShowAbout(false)}
            />

            <ContactModal
                isOpen={showContact}
                onClose={() => setShowContact(false)}
            />
        </motion.div>
    );
};

export default MainPage;
